import { useEffect, useState } from "react";
import { Sparkles, Sun, TrendingDown, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, eur, num } from "@/lib/api";

const today = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });
const itDate = (s) => new Date(`${s}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

const Stat = ({ id, label, value, delta }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`welcome-${id}`}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1 font-heading text-lg font-semibold text-slate-900">{value}</div>
    {delta !== null && delta !== undefined && (
      <div className={`text-xs font-medium ${delta >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`welcome-${id}-delta`}>{delta > 0 ? "+" : ""}{Number(delta).toFixed(1).replace(".", ",")}%</div>
    )}
  </div>
);

export const WelcomeDialog = () => {
  const { user } = useAuth();
  const key = `welcome-seen-${user?.id}`;
  const [open, setOpen] = useState(false);
  const [w, setW] = useState(null);
  const [ai, setAi] = useState(undefined);
  const done = () => { setTimeout(() => window.dispatchEvent(new Event("welcome-done")), 0); };
  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem(key) === today()) { done(); return; }
    api.get("/welcome").then((r) => {
      if (!r.data.enabled) { done(); return; }
      setW(r.data); setOpen(true);
      if (user?.features?.ai !== false) api.get("/welcome/ai", { timeout: 90000 }).then((x) => setAi(x.data.text)).catch(() => setAi(null)); else setAi(null);
    }).catch(done);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = () => { localStorage.setItem(key, today()); setOpen(false); done(); };
  if (!w) return null;
  const c = w.compare || {};
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-2xl bg-gradient-to-br from-white to-teal-50/60" data-testid="welcome-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Sun className="h-5 w-5 text-amber-500" />Buongiorno, {w.name}</DialogTitle>
        </DialogHeader>
        <p className="-mt-2 text-sm text-slate-500" data-testid="welcome-date">
          {w.stale ? "Ultimo giorno con dati" : "Ieri"}, {itDate(w.date)}{w.stores ? (w.stores === 1 ? " · il tuo punto vendita" : ` · i tuoi ${w.stores} punti vendita`) : ""}{c.label ? ` · vs ${c.label}` : ""}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Stat id="revenue" label="Valore vendite" value={eur(w.revenue)} delta={c.revenue} />
          <Stat id="transactions" label="Scontrini" value={num(w.transactions)} delta={c.transactions} />
          <Stat id="customers" label="Clienti" value={num(w.customers)} delta={c.customers} />
          <Stat id="avg-ticket" label="Scontrino medio" value={eur(w.avg_ticket)} delta={c.avg_ticket} />
        </div>
        {w.ranking && <div className="grid gap-2" data-testid="welcome-ranking">
          {[["best", "In crescita", "text-emerald-600", TrendingUp], ["worst", "In calo", "text-rose-600", TrendingDown]].map(([k, t, cls, I]) => (
            <div key={k} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`welcome-ranking-${k}`}>
              <div className={`mb-1.5 flex items-center gap-1 text-xs font-semibold ${cls}`}><I className="h-3.5 w-3.5" />{t} vs anno scorso</div>
              {w.ranking[k].map((r, i) => (
                <div key={r.id} className="flex items-center justify-between gap-2 py-0.5 text-xs" data-testid={`welcome-${k}-${i}`}>
                  <span className="truncate text-slate-700" title={r.name}>{i + 1}. {r.name}</span>
                  <span className={`shrink-0 font-medium ${r.growth >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{r.growth > 0 ? "+" : ""}{Number(r.growth).toFixed(1).replace(".", ",")}%</span>
                </div>
              ))}
            </div>
          ))}
        </div>}
        <div className="rounded-xl border border-teal-100 bg-white/80 p-3.5 text-sm text-slate-700" data-testid="welcome-ai">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-teal-700"><Sparkles className="h-3.5 w-3.5" />Sintesi IA del giorno</div>
          {ai === undefined ? <span className="text-slate-400">Sto leggendo i numeri...</span> : ai || <span className="text-slate-400">Sintesi non disponibile in questo momento.</span>}
        </div>
        <Button data-testid="welcome-close-button" className="bg-teal-600 hover:bg-teal-700" onClick={close}>Inizia la giornata</Button>
      </DialogContent>
    </Dialog>
  );
};
